'use client'

import {
  type ComponentProps,
  FormEvent,
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button } from '@components/ui/button/Button'
import { Input } from '@components/ui/input/Input'
import { Dialog, DialogContent } from '@components/shared/dialog'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import { usePrivyAuth } from '@domain/auth/context/usePrivyAuth.hook'
import { useCurrentBreakpoint } from '@hooks/useCurrentBreakpoint.hook'
import {
  useMyPreIpoInterest,
  useRegisterPreIpoInterest,
  useUpcomingWhitelistEligibility,
} from '@domain/pre-ipo-access/api/usePreIpoInterest'
import { useRegisterInterestSource } from '@domain/pre-ipo-access/utils/registerInterestSource'

type RegisterInterestDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  fundId: string
  fundName: string
  minInvestment?: number | string | null
  isUpcoming?: boolean
}

type RegisterInterestButtonProps = Omit<
  RegisterInterestDialogProps,
  'open' | 'onOpenChange'
> & {
  children?: ReactNode
  buttonClassName?: string
  buttonVariant?: ComponentProps<typeof Button>['variant']
  buttonSize?: ComponentProps<typeof Button>['size']
}

const getStoredReferralCode = () => {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem('referrer_code')
}

const getCurrentSearchParam = (...keys: string[]) => {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(window.location.search)
  return keys.map((key) => params.get(key)).find(Boolean) ?? null
}

const INTEREST_CONFIRMATION_MESSAGE =
  'Thank you for your interest! Look out for the launch date! Remember, this is non-binding and you can edit your interest any time prior to launch.'

const WHITELIST_DISCLAIMER =
  'On launch your account will be able to purchase this amount, subject to availability, for the first 24 hours. If less is available, your whitelisted amount will be pro-rated evenly across other whitelisted accounts. This is non-binding and will fall away within 24 hours of launch.'

const GENERAL_INTEREST_DISCLAIMER =
  'This is a non-binding indication of your interest and does not guarantee participation. Please come early to avoid missing out. Edit your interest any time prior to launch.'

const getInterestCopy = ({
  isUpcoming,
  hasExisting,
  isWhitelisted,
}: {
  isUpcoming: boolean
  hasExisting: boolean
  isWhitelisted: boolean
}) => {
  if (isUpcoming && isWhitelisted) {
    return {
      buttonLabel: hasExisting ? 'Edit Whitelist' : 'Claim Whitelist',
      dialogTitle: 'Claim Whitelist',
      submitLabel: hasExisting ? 'Update interest' : 'Claim Whitelist',
      disclaimer: WHITELIST_DISCLAIMER,
      confirmationMessage: INTEREST_CONFIRMATION_MESSAGE,
    }
  }

  return {
    buttonLabel: hasExisting ? 'Edit Interest' : 'Register Interest',
    dialogTitle: 'Register Interest',
    submitLabel: hasExisting ? 'Update interest' : 'Register Interest',
    disclaimer: GENERAL_INTEREST_DISCLAIMER,
    confirmationMessage: INTEREST_CONFIRMATION_MESSAGE,
  }
}

export function RegisterInterestDialog({
  open,
  onOpenChange,
  fundId,
  fundName,
  minInvestment,
  isUpcoming = true,
}: RegisterInterestDialogProps) {
  const isAuthenticated = useIsAuthenticated()
  const { triggerLogin } = usePrivyAuth()
  const myInterestQuery = useMyPreIpoInterest(fundId)
  const eligibilityQuery = useUpcomingWhitelistEligibility(fundId)
  const registerInterest = useRegisterPreIpoInterest(fundId)
  const [interestAmount, setInterestAmount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const minimumAmount = Number(minInvestment) > 0 ? Number(minInvestment) : 1

  const source = useRegisterInterestSource()

  const referralCode = useMemo(
    () =>
      getCurrentSearchParam('referrer_code', 'referral', 'ref', 'code') ||
      getStoredReferralCode(),
    [],
  )

  useEffect(() => {
    if (!open) {
      setError(null)
      setSubmitError(null)
      setSubmitted(false)
      return
    }

    if (myInterestQuery.data?.interestAmount) {
      setInterestAmount(String(myInterestQuery.data.interestAmount))
    }
  }, [myInterestQuery.data?.interestAmount, open])

  const validate = () => {
    const parsedAmount = Number(interestAmount)

    if (!interestAmount || Number.isNaN(parsedAmount)) {
      setError('Enter an amount.')
      return false
    }

    if (parsedAmount < minimumAmount) {
      setError(`Enter at least $${minimumAmount.toLocaleString()}.`)
      return false
    }

    setError(null)
    return true
  }

  const submitInterest = async () => {
    if (!validate()) return

    setSubmitError(null)

    try {
      await registerInterest.mutateAsync({
        fundUrlId: fundId,
        fundName,
        interestAmount: Number(interestAmount),
        referralCode,
        source,
        minInvestmentValue: minimumAmount,
      })
      setSubmitted(true)
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : 'We could not save your interest. Please try again.',
      )
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!isAuthenticated) {
      triggerLogin()
      return
    }

    void submitInterest()
  }

  const existingInterest = myInterestQuery.data?.interestAmount
  const isWhitelisted =
    myInterestQuery.data?.status === 'whitelisted' ||
    !!eligibilityQuery.data?.isWhitelisted
  const { dialogTitle, submitLabel, disclaimer, confirmationMessage } = getInterestCopy({
    isUpcoming,
    hasExisting: !!existingInterest,
    isWhitelisted,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent isClosable className="!bg-white xs:!max-w-[520px] xs:p-10">
        {submitted ? (
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-14 h-14 rounded-full bg-cw-eucal-green/15 flex items-center justify-center mb-5">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-cw-eucal-green"
                />
              </svg>
            </div>
            <p className="font-funnel text-sm text-cw-midnight-purple/80 mb-6">
              {confirmationMessage}
            </p>
            <Button
              type="button"
              variant="primary"
              className="w-full sm:w-auto"
              onClick={() => onOpenChange(false)}
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 className="font-funnel text-h3 font-semibold text-cw-midnight-purple">
              {dialogTitle}
            </h2>

            <div className="mt-6">
              <label className="mb-2 block font-funnel text-sm font-semibold text-cw-midnight-purple">
                Amount (USD)
              </label>
              <Input
                variant="unstyled"
                type="number"
                min={minimumAmount}
                step="1"
                placeholder={`Minimum $${minimumAmount.toLocaleString()}`}
                value={interestAmount}
                onChange={(event) => {
                  setInterestAmount(event.target.value)
                  setError(null)
                  setSubmitError(null)
                }}
                isInvalid={!!error}
                className="!h-14 !px-5 !py-4 rounded-xl font-funnel text-base"
              />
              {error && (
                <p className="mt-2 font-funnel text-sm text-cw-system-red">{error}</p>
              )}
            </div>

            {submitError && (
              <p className="mt-4 font-funnel text-sm text-cw-system-red">{submitError}</p>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Button
                type="submit"
                variant="primary"
                className="w-full sm:w-auto"
                disabled={registerInterest.isPending}
              >
                {registerInterest.isPending ? 'Saving...' : submitLabel}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </div>

            <p className="mt-4 font-funnel text-sm italic text-cw-midnight-purple/60">
              {disclaimer}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function RegisterInterestButtonInner({
  fundId,
  fundName,
  minInvestment,
  children,
  buttonClassName,
  buttonVariant = 'green',
  buttonSize,
  isUpcoming = true,
}: RegisterInterestButtonProps) {
  const isAuthenticated = useIsAuthenticated()
  const { triggerLogin } = usePrivyAuth()
  const { isMobile } = useCurrentBreakpoint()
  const resolvedSize = buttonSize ?? (isMobile ? 'sm' : 'md')
  const [open, setOpen] = useState(false)
  const myInterestQuery = useMyPreIpoInterest(fundId)
  const eligibilityQuery = useUpcomingWhitelistEligibility(fundId)
  useRegisterInterestSource()

  const { buttonLabel } = getInterestCopy({
    isUpcoming,
    hasExisting: !!myInterestQuery.data?.interestAmount,
    isWhitelisted:
      myInterestQuery.data?.status === 'whitelisted' ||
      !!eligibilityQuery.data?.isWhitelisted,
  })
  const label = children ?? buttonLabel

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={resolvedSize}
        className={buttonClassName}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()

          if (!isAuthenticated) {
            triggerLogin({ onAuthSuccess: () => setOpen(true) })
            return
          }

          setOpen(true)
        }}
      >
        {label}
      </Button>
      <RegisterInterestDialog
        open={open}
        onOpenChange={setOpen}
        fundId={fundId}
        fundName={fundName}
        minInvestment={minInvestment}
        isUpcoming={isUpcoming}
      />
    </>
  )
}

export function RegisterInterestButton(props: RegisterInterestButtonProps) {
  return (
    <Suspense
      fallback={
        <Button
          type="button"
          variant={props.buttonVariant ?? 'green'}
          size={props.buttonSize ?? 'md'}
          className={props.buttonClassName}
          disabled
        >
          {props.children ?? 'Register Interest'}
        </Button>
      }
    >
      <RegisterInterestButtonInner {...props} />
    </Suspense>
  )
}
